import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MobileTailwind from "./MobileTailwind";
import PrimaryButton from "./components/PrimaryButton";
import SubHeader from "./components/SubHeader";

interface ReceiptItem {
  name: string;
  qty: number;
  amount: number;
}

const ITEMS: ReceiptItem[] = [
  { name: "Paneer Tikka", qty: 1, amount: 120 },
  { name: "Thecha Paratha", qty: 1, amount: 100 },
];

const CheckCircle = () => (
  <svg width="65" height="65" viewBox="0 0 65 65" fill="none">
    <circle cx="32.5" cy="32.5" r="30" stroke="#67AF7A" strokeWidth="3" fill="none" />
    <path d="M20 33.5l9 9 16-18" stroke="#67AF7A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const Receipt = () => {
  const navigate = useNavigate();
  const { tableNo = "T1" } = useParams<{ tableNo: string }>();
  const [searchParams] = useSearchParams();
  const amountParam = searchParams.get("amount");
  const total = useMemo(() => {
    if (amountParam) return Number(amountParam);

    return ITEMS.reduce((acc, it) => acc + it.amount * it.qty, 0);
  }, [amountParam]);


  const handleDownload = () => {
    const rows = ITEMS.map(
      (it) => `
        <tr>
          <td>${it.name}</td>
          <td style="text-align:center">${it.qty}</td>
          <td style="text-align:right;font-weight:700">${(it.amount * it.qty).toFixed(2)}</td>
        </tr>`
    ).join("");

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Receipt - Table ${tableNo}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Open Sans', Arial, sans-serif; color: #111; margin: 0; padding: 24px; background: #fff; }
    .wrap { max-width: 360px; margin: 0 auto; }
    .brand { text-align: center; margin-bottom: 8px; font-weight: 700; font-size: 18px; letter-spacing: 1px; color: #29b1c4; }
    .confirm { text-align: center; font-weight: 700; font-size: 18px; margin: 12px 0 20px; }
    .card { border: 1px solid #e5e5e5; border-radius: 10px; padding: 16px; }
    h2 { text-align: center; margin: 0 0 12px; font-size: 18px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { text-align: left; color: rgba(17,17,17,0.6); font-weight: 400; padding: 6px 0; border-bottom: 1px dashed #ddd; }
    th:nth-child(2) { text-align: center; }
    th:nth-child(3) { text-align: right; }
    td { padding: 10px 0; border-bottom: 1px dashed #ddd; }
    tfoot td { font-weight: 700; border-bottom: none; padding-top: 12px; }
    .meta { text-align: center; color: rgba(17,17,17,0.6); font-size: 12px; margin-top: 16px; }
    @page { margin: 12mm; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="brand">F.M.L</div>
    <div class="confirm">Payment Confirmed!</div>
    <div class="card">
      <h2>Receipt</h2>
      <table>
        <thead>
          <tr><th>Name</th><th>Quantity</th><th>Amount</th></tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td></td>
            <td style="text-align:right">${total.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div class="meta">Table ${tableNo} &middot; ${new Date().toLocaleString()}</div>
  </div>
  <script>
    window.onload = function() {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>`;

    const win = window.open("", "_blank", "width=420,height=720");
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <>
      <MobileTailwind />
      <div id="mobile-root" className="min-h-screen w-full bg-black flex justify-center">
        <div className="relative w-full min-h-screen overflow-x-hidden flex flex-col" style={{ backgroundColor: "#F4F4F5" }}>
          {/* Header */}
          <SubHeader title="Receipt" onBack={() => navigate("/mobile/tables")} />

          {/* Confirmation banner */}
          <div className="bg-white h-[139px] flex flex-col items-center justify-center gap-3 shrink-0">
            <CheckCircle />
            <p className="font-sansation font-bold text-[#111] text-[20px]">
              Payment Confirmed!
            </p>
          </div>

          {/* Body */}
          <div className="flex-1 px-3 pt-3 pb-[180px]">
            {/* Receipt card */}
            <div className="bg-white rounded-[6px] border border-[#E6EEFF] p-4">
              <h2 className="font-sansation font-bold text-[#111] text-[20px] text-center">
                Receipt
              </h2>

              <div className="mt-4 pt-3" style={{ borderTop: "1px dashed #DDDDDD" }}>
                {/* Column headers */}
                <div className="flex items-center font-opensans text-[14px] text-[rgba(17,17,17,0.6)] pb-2">
                  <span className="flex-1">Name</span>
                  <span className="w-[70px] text-left">Quantity</span>
                  <span className="w-[80px] text-right">Amount</span>
                </div>

                <div style={{ borderTop: "1px dashed #DDDDDD" }}>
                  {ITEMS.map((it) => (
                    <div key={it.name}
                      className="flex items-center py-3 font-opensans text-[14px] text-[#111]"
                    >
                      <span className="flex-1">{it.name}</span>
                      <span className="w-[70px] text-left">{it.qty}</span>
                      <span className="w-[80px] text-right font-sansation font-bold text-[16px]">
                        {(it.amount * it.qty).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center pt-3 font-opensans text-[14px] text-[#111]" style={{ borderTop: "1px dashed #DDDDDD" }}>
                  <span className="flex-1">Total</span>
                  <span className="w-[70px]" />
                  <span className="w-[80px] text-right font-sansation font-bold text-[16px]">
                    {total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky bottom CTAs */}
          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full bg-white px-4 py-4 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] flex flex-col gap-3">
            <PrimaryButton onClick={handleDownload}>
              Download
            </PrimaryButton>
            <PrimaryButton
              variant="outline"
              onClick={() => navigate(`/mobile/email-receipt/${tableNo}`)}
            >
              Email/Whatsapp Receipt
            </PrimaryButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default Receipt;
